import { Fragment, useState } from 'react';
import { useOwnTrunks, useDeleteOwnTrunk } from '../../lib/api/hooks-products.js';
import { TableSkeleton, Empty, ErrorBanner, SectionHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { useSession } from '../../lib/auth/session.js';
import { TrunkEditor } from './TrunkEditor.js';
import { OwnTrunkWizard } from './OwnTrunkWizard.js';
import { TrunkNumberOffer } from './TrunkNumberOffer.js';
import { HealthBadge, RegistrationBadge } from './TrunkLive.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * A customer's own trunks (bring your own trunk), with the switch's live view
 * of each. The same panel in the customer's portal, where they set their
 * carrier account up, and on the account's Trunks tab, where staff see -- and
 * can fix -- exactly what the customer sees. Writing needs connectivity:write.
 */
export function OwnTrunksPanel({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const own = useOwnTrunks(tenantId);
  const remove = useDeleteOwnTrunk(tenantId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const mayWrite = can('connectivity:write');
  const rows = own.data?.data ?? [];

  return (
    <>
      <SectionHead title="Own trunk"
        sub="The customer’s own carrier account. Their carrier bills these calls; ArcNet does not."
        action={mayWrite && !adding && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setAdding(true)}>
            <Icon name="plus" size={13} /> Add trunk
          </button>
        )} />

      {own.error && <ErrorBanner error={own.error} />}
      {adding && <OwnTrunkWizard tenantId={tenantId} existing={rows.map((t) => t.name)} onDone={() => setAdding(false)} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Trunk</th><th>Carrier</th><th>Used for</th><th>Status</th><th>Registration</th>
                <th className="arc-td-right">Uptime 24 h</th><th />
              </tr>
            </thead>
            {own.data === undefined ? <TableSkeleton cols={7} /> : (
              <tbody>
                {rows.map((t) => (
                  <Fragment key={t.id}>
                    <tr style={t.status === 'active' ? undefined : { opacity: 0.55 }}>
                      <td>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{t.name}</span>
                          <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{t.host}:{t.port}</span>
                          {t.inbound_number && <span className="arc-subtle" style={{ fontSize: 11.5 }}>Inbound: <span className="arc-mono">{t.inbound_number.e164}</span></span>}
                        </div>
                      </td>
                      <td>{t.provider_name}</td>
                      <td>{t.direction === 'both' ? 'in and out' : t.direction}</td>
                      <td><HealthBadge live={t.live} /></td>
                      <td><RegistrationBadge live={t.live} authMode={t.auth_mode} /></td>
                      <td className="arc-td-right arc-num">
                        {t.live?.uptime_24h_pct != null ? `${t.live.uptime_24h_pct}%` : '—'}
                      </td>
                      <td className="arc-td-right">
                        {mayWrite && (
                          <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                            <button type="button" className="arc-btn arc-btn-sm arc-pill-edit"
                              onClick={() => setEditing(editing === t.id ? null : t.id)}>
                              {editing === t.id ? 'Close' : 'Configure'}
                            </button>
                            <RowAction kind="delete" label={`Delete ${t.name}`}
                              onClick={() => {
                                void confirmAction({ title: `Delete ${t.name}?`, body: 'Calls stop using it within a minute.', action: 'Delete', danger: true }).then((ok) => { if (ok) remove.mutate(t.id); });
                              }} />
                          </div>
                        )}
                      </td>
                    </tr>
                    {editing === t.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          {!t.inbound_number && (
                            <div className="arc-card-body" style={{ borderBottom: '1px solid var(--arc-border)' }}>
                              <TrunkNumberOffer tenantId={tenantId} trunk={t} />
                            </div>
                          )}
                          <TrunkEditor trunk={t} own={{ tenantId }} onDone={() => setEditing(null)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {own.isSuccess && rows.length === 0 && (
          <Empty icon="carrier" title="No trunk yet"
            {...(mayWrite ? { hint: 'Add the host, login and addresses your carrier gave you.' } : {})} />
        )}
      </div>
    </>
  );
}
